// Firebase Storage helpers. All paths are scoped under users/{uid}/...
// so storage.rules can stay one line. compressImage runs in the browser
// (canvas) — no native dependency, no install. Default 1600px / 0.82 JPEG
// is the spec target for inspection photos (Phase 2).
import {
  ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { storage, auth } from '../firebase.js';

function userPath(rest) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not signed in.');
  if (!rest || rest.startsWith('/')) {
    throw new Error('storage path must be a non-empty relative path');
  }
  return `users/${uid}/${rest}`;
}

export async function uploadFile(path, blobOrFile, metadata = {}) {
  const r = ref(storage, userPath(path));
  await uploadBytes(r, blobOrFile, metadata);
  return getDownloadURL(r);
}

// Same as uploadFile but yields progress (0..1) via the onProgress
// callback. Use when the user is actively waiting and a percentage
// indicator would help (logo upload, future photo upload, etc.).
export function uploadFileWithProgress(path, blobOrFile, metadata = {}, onProgress) {
  return new Promise((resolve, reject) => {
    const r = ref(storage, userPath(path));
    const task = uploadBytesResumable(r, blobOrFile, metadata);
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress && snap.totalBytes > 0) {
          onProgress(snap.bytesTransferred / snap.totalBytes);
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        } catch (e) {
          reject(e);
        }
      }
    );
  });
}

export async function getUrl(path) {
  return getDownloadURL(ref(storage, userPath(path)));
}

export async function removeFile(path) {
  await deleteObject(ref(storage, userPath(path)));
}

// Delete many files, best-effort.
//
// Storage has no batch delete, so this is one request per file. It
// never throws: these calls always accompany a Firestore delete, and
// failing to clean up a photo must not stop the record itself from
// being removed. A file already gone (a retried delete) is a success,
// not an error.
//
// Returns how many were actually removed, for logging.
export async function removeFiles(paths) {
  const list = (Array.isArray(paths) ? paths : []).filter(Boolean);
  if (list.length === 0) return 0;
  const results = await Promise.allSettled(list.map((p) => removeFile(p)));
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    console.warn(`[storage] ${failed.length}/${list.length} file deletes failed`, failed[0].reason);
  }
  return results.length - failed.length;
}

export async function compressImage(file, { maxDim = 1600, quality = 0.82 } = {}) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      quality,
    );
  });
}
